import React, { useState } from "react";
import toast from "react-hot-toast";

import documentsService from "@/services/documentsService";
import FleetService from "@/services/FleetService";

interface Vehicle {
  assignedDriver: string | { full_name?: string; id?: string; name?: string };
  brand: string;
  color: string;
  fuel_type: string;
  id: string;
  insurance_number: string;
  max_load: string;
  mileage: string;
  model: string;
  plate_number: string;
  registrationDocument?: null | string;
  status: "active" | "inactive" | "maintenance";
  type: string;
  year: string;
}

interface VehicleViewModalProps {
  onClose: () => void;
  onDelete?: (vehicleId: string) => void;
  onRefresh?: () => void;
  open: boolean;
  vehicle: null | Vehicle;
}

const VehicleViewModal: React.FC<VehicleViewModalProps> = ({
  onClose,
  onDelete,
  onRefresh,
  open,
  vehicle,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  if (!open || !vehicle) return null;

  const handleDelete = async () => {
    if (!vehicle?.id) return;

    setIsDeleting(true);
    try {
      await FleetService.deleteVehicle(vehicle.id);
      toast.success("Vehicle deleted successfully");
      setShowDeleteConfirm(false);
      onClose();
      if (onDelete) onDelete(vehicle.id);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error deleting vehicle:", error);
      toast.error("Failed to delete vehicle");
    } finally {
      setIsDeleting(false);
    }
  };

  const info = [
    { label: "Brand", value: vehicle.brand },
    { label: "Model", value: vehicle.model },
    { label: "Year", value: vehicle.year },
    { label: "Type", value: vehicle.type },
    { label: "Color", value: vehicle.color },
    { label: "Plate Number", value: vehicle.plate_number },
    { label: "Fuel Type", value: vehicle.fuel_type },
    { label: "Max Load (kg)", value: vehicle.max_load },
    {
      label: "Assigned Driver",
      value:
        typeof vehicle.assignedDriver === "object" &&
        vehicle.assignedDriver !== null
          ? vehicle.assignedDriver.full_name ||
            vehicle.assignedDriver.name ||
            vehicle.assignedDriver.id ||
            "Unassigned"
          : vehicle.assignedDriver || "Unassigned",
    },
    { label: "Insurance Number", value: vehicle.insurance_number },
    // Registration Document field temporarily removed
    { label: "Status", value: vehicle.status },
    { label: "Mileage", value: vehicle.mileage },
  ];

  // Helper to get full document URL
  const getDocumentUrl = (path: string) => {
    if (!path) return "";
    // If path is already absolute, return as is
    if (/^https?:\/\//.test(path)) return path;
    // Otherwise, assume relative to public folder
    return `${window.location.origin}/${path}`;
  };

  const handleViewDocument = async () => {
    if (!vehicle?.id) {
      toast.error("No registration document found.");
      return;
    }
    try {
      const blob = await documentsService.downloadVehicleRegistrationDocument(
        vehicle.id,
      );
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      toast.error("Failed to view document. Please try again.");
    }
  };

  const handleDownloadDocument = async () => {
    if (!vehicle?.id) {
      toast.error("No registration document found.");
      return;
    }
    try {
      const blob = await documentsService.downloadVehicleRegistrationDocument(
        vehicle.id,
      );
      const filename = `registration_document_${vehicle.plate_number || vehicle.id || "document"}.pdf`;
      documentsService.downloadFile(blob, filename);
    } catch (error) {
      toast.error("Failed to download document. Please try again.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-4 shadow-2xl dark:bg-gray-900 sm:max-w-xl sm:p-6 md:max-w-2xl md:p-8 lg:max-w-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-6 text-2xl font-bold text-gray-900 dark:text-gray-100">
          Vehicle Details
        </h2>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {info.map((item, idx) => (
            <div className="flex flex-col" key={idx}>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {item.label}
              </span>
              <div className="break-words font-medium text-gray-900 dark:text-gray-100">
                {item.value}
              </div>
            </div>
          ))}
        </div>

        {/* Registration Document Section */}
        {vehicle.registrationDocument && (
          <div className="mt-8 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
            <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
              Registration Document
            </h3>
            <div className="flex flex-wrap items-center gap-4">
              <button
                className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                onClick={handleViewDocument}
              >
                View
              </button>
              <button
                className="rounded bg-green-600 px-4 py-2 text-white hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600"
                onClick={handleDownloadDocument}
              >
                Download
              </button>
              <label className="inline-flex cursor-pointer items-center">
                <input
                  accept="application/pdf"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (!vehicle?.id) {
                      toast.error("Vehicle ID missing.");
                      return;
                    }
                    try {
                      await documentsService.uploadVehicleRegistrationDocument(
                        vehicle.id,
                        file,
                      );
                      toast.success("Registration document updated.");
                      if (onRefresh) onRefresh();
                    } catch (error) {
                      toast.error(
                        "Failed to upload document. Please try again.",
                      );
                    }
                  }}
                  type="file"
                />
                <span className="ml-0 rounded bg-purple-600 px-4 py-2 text-white hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600">
                  Replace
                </span>
              </label>
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-between">
          <button
            className="rounded-lg bg-red-600 px-6 py-2 font-semibold text-white shadow transition hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600"
            onClick={() => setShowDeleteConfirm(true)}
          >
            Delete Vehicle
          </button>
          <button
            className="rounded-lg bg-blue-600 px-6 py-2 font-semibold text-white shadow transition hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="z-60 fixed inset-0 flex items-center justify-center bg-black bg-opacity-60">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-900">
            <div className="mb-4 flex items-center">
              <div className="mr-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900">
                <svg
                  className="h-6 w-6 text-red-600 dark:text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Delete Vehicle
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  This action cannot be undone
                </p>
              </div>
            </div>

            <p className="mb-6 text-gray-700 dark:text-gray-300">
              Are you sure you want to delete{" "}
              <span className="font-semibold">
                {vehicle.brand} {vehicle.model}
              </span>{" "}
              ({vehicle.plate_number})? This will permanently remove the vehicle
              and all associated data.
            </p>

            <div className="flex justify-end space-x-3">
              <button
                className="rounded-lg bg-gray-300 px-4 py-2 font-medium text-gray-700 transition hover:bg-gray-400 disabled:opacity-50 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
                disabled={isDeleting}
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="rounded-lg bg-red-600 px-4 py-2 font-medium text-white transition hover:bg-red-700 disabled:opacity-50 dark:bg-red-500 dark:hover:bg-red-600"
                disabled={isDeleting}
                onClick={handleDelete}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleViewModal;
